import { useEffect, useState, useRef } from 'react';

// Tipos dos eventos
export interface ScheduleUpdatedEvent {
  dentist_id: number;
  unit_id: number;
  date: string;
  action: 'create' | 'update' | 'delete' | 'status_change';
  data: {
    schedule_id: number | null;
    patient_id: number | null;
    open_count: number;
  };
  timestamp: string;
}

export interface PatientArrivedEvent {
  schedule_id: number;
  dentist_id: number;
  unit_id: number;
  patient_id: number;
  patient_name: string;
  arrival_time: string;
  timestamp: string;
}

export interface OpenSlotsUpdatedEvent {
  dentist_id: number;
  unit_id: number;
  open_slots: any;
  timestamp: string;
}

export interface DentistNotificationEvent {
  notification_id: number;
  dentist_id: number;
  unit_id: number;
  title: string;
  message: string;
  created_at: string;
  timestamp: string;
}

interface WebSocketConfig {
  host: string;
  port: number;
  key: string;
  dentistId: number;
  enabled?: boolean;
}

interface WebSocketCallbacks {
  onScheduleUpdated?: (event: ScheduleUpdatedEvent) => void;
  onPatientArrived?: (event: PatientArrivedEvent) => void;
  onOpenSlotsUpdated?: (event: OpenSlotsUpdatedEvent) => void;
  onDentistNotification?: (event: DentistNotificationEvent) => void;
  onConnected?: () => void;
  onDisconnected?: () => void;
  onError?: (error: any) => void;
}

// Variáveis globais do singleton
let globalWs: WebSocket | null = null;
let globalConnected = false;
let globalDentistId = 0;
let globalCallbacks: WebSocketCallbacks = {};
let processedEvents = new Set<string>();
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let pingTimer: ReturnType<typeof setInterval> | null = null;
let connectionInProgress = false;

function clearTimers() {
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
  if (pingTimer) {
    clearInterval(pingTimer);
    pingTimer = null;
  }
}

function connectWebSocket(config: WebSocketConfig) {
  if (!config.enabled || !config.dentistId) return;
  if (connectionInProgress) return;
  if (globalWs?.readyState === WebSocket.OPEN && globalDentistId === config.dentistId) return;
  if (globalWs?.readyState === WebSocket.CONNECTING) return;

  // Fechar conexão anterior
  if (globalWs) {
    globalWs.onclose = null;
    globalWs.close();
    globalWs = null;
  }
  clearTimers();

  connectionInProgress = true;
  globalDentistId = config.dentistId;

  const protocol = config.port === 443 ? 'wss' : 'ws';
  const wsUrl = `${protocol}://${config.host}:${config.port}/app/${config.key}?protocol=7&client=js&version=8.0.0&flash=false`;
  console.log('[WS] Conectando...', wsUrl);

  try {
    globalWs = new WebSocket(wsUrl);

    globalWs.onopen = () => {
      connectionInProgress = false;
      globalConnected = true;
      console.log('[WS] ✅ Conectado');
      globalCallbacks.onConnected?.();

      globalWs?.send(JSON.stringify({
        event: 'pusher:subscribe',
        data: { channel: `dentist.${globalDentistId}` }
      }));

      // Ping a cada 25s
      pingTimer = setInterval(() => {
        if (globalWs?.readyState === WebSocket.OPEN) {
          globalWs.send(JSON.stringify({ event: 'pusher:ping', data: {} }));
        }
      }, 25000);
    };

    globalWs.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);

        // Ignorar mensagens do protocolo Pusher
        if (data.event?.startsWith('pusher:') || data.event?.startsWith('pusher_internal:')) {
          return;
        }

        const eventData = typeof data.data === 'string' ? JSON.parse(data.data) : data.data;
        
        // Deduplicação: usar notification_id, schedule_id ou timestamp
        const eventKey =
          eventData.notification_id ||
          eventData.schedule_id ||
          eventData.timestamp ||
          'no-id';
        const eventId = `${data.event}-${eventKey}`;
        if (processedEvents.has(eventId)) {
          return; // Já processado
        }
        processedEvents.add(eventId);
        setTimeout(() => processedEvents.delete(eventId), 30000); // Limpar após 30s

        console.log('[WS] 📥 Evento recebido:', data.event, JSON.stringify(eventData));

        if (data.event === 'patient.arrived' || data.event === '.patient.arrived') {
          console.log('[WS] ✅ Processando patient.arrived');
          globalCallbacks.onPatientArrived?.(eventData);
        } else if (data.event === 'schedule.updated' || data.event === '.schedule.updated') {
          console.log('[WS] ✅ Processando schedule.updated');
          globalCallbacks.onScheduleUpdated?.(eventData);
        } else if (data.event === 'open_slots.updated' || data.event === '.open_slots.updated') {
          console.log('[WS] ✅ Processando open_slots.updated');
          globalCallbacks.onOpenSlotsUpdated?.(eventData);
        } else if (data.event === 'dentist.notification' || data.event === '.dentist.notification') {
          console.log('[WS] ✅ Processando dentist.notification');
          globalCallbacks.onDentistNotification?.(eventData);
        } else {
          console.log('[WS] ⚠️ Evento não reconhecido:', data.event);
        }
      } catch {
        // Ignorar erros de parse
      }
    };

    globalWs.onerror = () => {
      connectionInProgress = false;
    };

    globalWs.onclose = () => {
      connectionInProgress = false;
      globalConnected = false;
      globalWs = null;
      clearTimers();
      console.log('[WS] Desconectado');
      globalCallbacks.onDisconnected?.();

      // Reconectar após 5s
      if (globalDentistId > 0) {
        reconnectTimer = setTimeout(() => connectWebSocket(config), 5000);
      }
    };
  } catch {
    connectionInProgress = false;
  }
}

export function useWebSocket(config: WebSocketConfig, callbacks: WebSocketCallbacks) {
  const [isConnected, setIsConnected] = useState(globalConnected);
  const configRef = useRef(config);
  const mountedRef = useRef(true);

  // Atualizar config ref
  useEffect(() => {
    configRef.current = config;
  }, [config]);

  // Atualizar callbacks globais
  useEffect(() => {
    globalCallbacks = callbacks;
  }, [callbacks]);

  // Conectar uma única vez
  useEffect(() => {
    mountedRef.current = true;

    // Pequeno delay para evitar double-mount do StrictMode
    const timer = setTimeout(() => {
      if (mountedRef.current && config.enabled && config.dentistId) {
        connectWebSocket(config);
      }
    }, 200);

    // Atualizar estado local periodicamente
    const stateInterval = setInterval(() => {
      if (mountedRef.current) {
        setIsConnected(globalConnected);
      }
    }, 1000);

    return () => {
      mountedRef.current = false;
      clearTimeout(timer);
      clearInterval(stateInterval);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.enabled, config.dentistId, config.host, config.port, config.key]);

  return { isConnected, reconnect: () => connectWebSocket(configRef.current), disconnect: clearTimers };
}
